import _ from 'lodash';
import {
  detectVolume,
  float32ToInt16,
  findDefaultInput,
  findDefaultOutput
} from './utils';

// Monkey patch Web Audio
try {
  window.AudioContext = window.AudioContext || window.webkitAudioContext;
} catch (e) {
  console.log('Unable to patch window.AudioContext. Details: ' + e);
}

// Monkey patch getUserMedia
try {
  navigator.getUserMedia =
    navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia;
} catch (e) {
  console.log('Unable to patch navigator.getUserMedia. Details: ' + e);
}

// Web audio keys
const AUDIO_INPUT = "audioinput";
const AUDIO_OUTPUT = "audiooutput";

// Audio constants
const NUM_INPUT_CHANNELS = 1, NUM_OUTPUT_CHANNELS = 1;
const BUFFER_SIZE = 16384; //1024;

// Audio pipeline elements
let _audioContext;
let _processor;
let _destination;
let _source;
let _player;
try {
  _audioContext = new window.AudioContext();
} catch (e) {
  console.log('Audio context not supported in this browser. Details: ' + e);
}
if (_audioContext) {
  _processor = _audioContext.createScriptProcessor(
    BUFFER_SIZE, NUM_INPUT_CHANNELS, NUM_OUTPUT_CHANNELS);
  _destination = _audioContext.destination;
}
try {
  _player = new Audio();
} catch (e) {
  console.log('Unable to create audio player. Details: ' + e);
}

class AudioClient {

  stream;
  input;
  output;
  inputs;
  outputs;
  processorEnabled;
  playerEnabled;
  onAudio;

  setInput = input => this.input = input;

  setOutput = output => this.output = output;

  initialize = (onAudio) => {

    // Save internal state.
    this.onAudio = onAudio;

    return new Promise((resolve, reject) => {

      if (!_audioContext) {
        reject(
          "Audio context not supported in this browser. " +
          "Try with a different one (ex: Google Chrome)"
        );
      } else {
        this.discoverInputsAndOutputs()
          .then(({inputs, outputs}) => {
            // Find default devices and create pipeline
            const input = findDefaultInput(inputs);
            const {output, playOutput} = findDefaultOutput(outputs);

            // Save internal state.
            this.input = input;
            this.output = output;
            this.inputs = inputs;
            this.outputs = outputs;

            // Initialize processor and player state
            this.processorEnabled = true;
            playOutput ? this.startPlayer() : this.stopPlayer();

            // Resolve promise with internal state to be displayed in browser.
            const playerEnabled = this.playerEnabled;
            resolve({input, output, inputs, outputs, playerEnabled});
          })
          .catch(error => reject(error));
      }
    });
  };

  discoverInputsAndOutputs = () => {
    return new Promise((resolve, reject) => {

      navigator.mediaDevices.enumerateDevices()
        .then(allDevices => {
          const inputs = [];
          const outputs = [];

          allDevices.forEach(d => {
            if (d.kind === AUDIO_INPUT) {
              inputs.push(d);
            }
            if (d.kind === AUDIO_OUTPUT) {
              outputs.push(d);
            }
          });
          resolve({inputs, outputs});
        })
        .catch(error => {
          reject(".enumerateDevices() error: " + error);
        });
    });
  };

  _disconnectPipeline = () => {
    try {
      _source.disconnect(_processor);
    } catch (e) {
      console.log('Unable to disconnect source/processor. Details: ' + e);
    }
    try {
      _processor.disconnect(_destination);
    } catch (e) {
      console.log('Unable to disconnect processor/destination. Details: ' + e);
    }
  };

  createPipeline = () => {
    if (!_audioContext) {
      console.log('No audio context for this browser. No pipeline created.');
      return;
    }

    // Before anything, disconnect pipeline
    this._disconnectPipeline();

    return new Promise((resolve, reject) => {

      const sourceId = _.get(this.input, 'deviceId');
      console.log('SourceId: ' + sourceId +
        ' | Sample rate: ' + _audioContext.sampleRate);
      if (!sourceId) reject('Unable to find audio source.');

      // Get user media
      const userMedia = {audio: {deviceId: sourceId}};
      navigator.mediaDevices.getUserMedia(userMedia)
        .then(stream => {

          this.stream = stream;

          // Set source
          _source = _audioContext.createMediaStreamSource(stream);

          // Processor
          _processor.onaudioprocess = e => {

            if (!this.processorEnabled) return;

            const buf = e.inputBuffer.getChannelData(0);

            // Send payload
            const volume = detectVolume(buf, _audioContext);
            const _audioBuffer = float32ToInt16(buf);
            const audioBuffer = new Uint8Array(_audioBuffer);
            this.onAudio({volume, audioBuffer});

          };

          // Make the pipeline
          if (_source && _processor) _source.connect(_processor);
          if (_processor && _destination) _processor.connect(_destination);

          // Set the sinkId and start the player
          if (_player) {
            _player.src = window.URL
              ? window.URL.createObjectURL(stream)
              : stream;
            const sinkId = _.get(this.output, 'deviceId');
            console.log('SinkId: ' + sinkId);

            // There might not be a valid sink, but that's ok, just skip it.
            if (sinkId) {
              _player.setSinkId(sinkId).then(() => {
                if (this.playerEnabled) this.startPlayer();
                console.log('Player sinkId: ' + _player.sinkId);
              });
            }
          }


          // Resolve promise
          resolve();
        })
        .catch(error => {
          reject('Could not get audio stream. Details: ' + error);
        });
    });
  };

  startPlayer = () => {
    if (_player) {
      _player.play();
      this.playerEnabled = true;
      console.log('Started player - sinkId: ' + _player.sinkId);
    }
  };

  stopPlayer = () => {
    if (_player) {
      _player.pause();
      this.playerEnabled = false;
      console.log('Stopped player - sinkId: ' + _player.sinkId);
    }
  };
}

export default new AudioClient();



// WEBPACK FOOTER //
// ./src/containers/Subtitles/audioClient.js